import PropTypes from 'prop-types';
import React, { Component, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Collapse, Divider, IconButton } from '@material-ui/core';
import MaterialGrid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import HotelIcon from '@material-ui/icons/Hotel';
import { Field, FieldArray, Form, getFormValues, reduxForm } from 'redux-form';
import { isMobile } from 'react-device-detect';
import PrimaryButton from 'common/components/buttons/primaryButton';
import SecondaryButton from 'common/components/buttons/secondaryButton';
import GenericConfirmationCard from 'common/components/cards/genericConfirmationModel/genericConfirmationModel';
import { InputLabelOutlined } from 'common/components/fields/InputLabelOutlined';
import { InputSwitchLarge } from 'common/components/fields/InputSwitchLarge';
import { FORM_RULES } from 'helpers/validations';
import { DEFAULT_HOTEL_ROOM_RULES } from '../config';

const sectionTitleStyle = {
  fontSize: 14,
  fontWeight: '600',
  marginBottom: 12,
  color: '#1a3a5c',
};

const hotelCardStyle = {
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: 16,
  marginBottom: 16,
  backgroundColor: '#fff',
};

const hotelHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
};

const tierCardStyle = {
  border: '1px solid #e8edf2',
  borderRadius: 6,
  padding: '12px 16px',
  marginBottom: 10,
  backgroundColor: '#fafcff',
};

const tierLabelStyle = {
  fontSize: 12,
  fontWeight: 600,
  color: '#1a3a5c',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

const ordinal = (n) => `${n}º`;

const getTierLabel = (index, totalCount) => {
  if (totalCount === 1) return 'Todos os quartos adicionais';
  if (index === totalCount - 1) return 'Demais quartos adicionais';
  return `${ordinal(index + 1)} quarto adicional`;
};

function renderPricingTiers({ fields }) {
  const totalCount = fields.length;

  return (
    <Box>
      {fields.map((tierField, tierIndex) => (
        <Box key={tierField} style={tierCardStyle}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography style={tierLabelStyle}>{getTierLabel(tierIndex, totalCount)}</Typography>
            {totalCount > 1 && tierIndex === totalCount - 1 && (
              <IconButton size="small" onClick={() => fields.remove(tierIndex)}>
                <DeleteOutlineIcon fontSize="small" style={{ color: '#c62828' }} />
              </IconButton>
            )}
          </Box>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Valor por quarto (R$)"
                name={`${tierField}.room_price`}
                type="number"
                validate={[FORM_RULES.required]}
                helperText="0 para não cobrar pelo quarto em si"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Valor por acompanhante (R$)"
                name={`${tierField}.companion_price`}
                type="number"
                validate={[FORM_RULES.required]}
                helperText="0 para não cobrar por acompanhante neste quarto"
              />
            </MaterialGrid>
          </MaterialGrid>
        </Box>
      ))}
      <SecondaryButton
        type="button"
        onClick={() => {
          const lastTier = fields.get(fields.length - 1) || { room_price: 0, companion_price: 0 };
          fields.push({
            room_price: lastTier.room_price,
            companion_price: lastTier.companion_price,
          });
        }}>
        + Adicionar faixa de quarto
      </SecondaryButton>
      <Typography style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
        A última faixa se aplica a todos os quartos adicionais além das faixas configuradas.
      </Typography>
    </Box>
  );
}

renderPricingTiers.propTypes = {
  fields: PropTypes.object,
};

function HotelCard({ field, index, hotel, onRemove, defaultExpanded }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const displayName = hotel?.name || `Hotel ${index + 1}`;

  return (
    <Box style={hotelCardStyle}>
      <Box style={hotelHeaderStyle} onClick={() => setExpanded((v) => !v)}>
        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
          <HotelIcon style={{ color: '#1a3a5c' }} />
          <Typography style={{ fontWeight: 700, color: '#1a3a5c' }}>{displayName}</Typography>
        </Box>
        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}>
            <DeleteOutlineIcon fontSize="small" style={{ color: '#c62828' }} />
          </IconButton>
          <ExpandMoreIcon
            style={{
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              color: '#1a3a5c',
            }}
          />
        </Box>
      </Box>

      <Collapse in={expanded} mountOnEnter>
        <Box mt={2}>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={8}>
              <Field
                component={InputLabelOutlined}
                label="Nome do hotel"
                name={`${field}.name`}
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.active`}
                label="Disponível para inscrição"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12}>
              <Field
                component={InputLabelOutlined}
                label="Descrição (opcional)"
                name={`${field}.description`}
                helperText="Aparece como subtítulo no card de seleção"
              />
            </MaterialGrid>
          </MaterialGrid>

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Capacidade e faixa etária</Typography>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Limite total de pessoas por quarto"
                name={`${field}.room_rules.room_people_limit`}
                type="number"
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Idade limite para menor"
                name={`${field}.room_rules.minor_age_limit`}
                type="number"
                validate={[FORM_RULES.required]}
                helperText="Considera menor quando a diferença por ano de nascimento for menor ou igual a este valor"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputLabelOutlined}
                label="Limite de acompanhantes pagantes no quarto 1"
                name={`${field}.room_rules.first_room.paying_companion_limit`}
                type="number"
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
          </MaterialGrid>

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Cobrança do primeiro quarto</Typography>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Valor por acompanhante pagante no quarto 1 (R$)"
                name={`${field}.room_rules.first_room.companion_price`}
                type="number"
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Limite de acompanhantes pagantes por quarto adicional"
                name={`${field}.room_rules.additional_room.paying_companion_limit`}
                type="number"
                validate={[FORM_RULES.required]}
              />
            </MaterialGrid>
          </MaterialGrid>

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Cobrança dos quartos adicionais</Typography>
          <MaterialGrid container spacing={2} style={{ marginBottom: 8 }}>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.room_rules.additional_room.charge_enabled`}
                label="Cobrar quartos adicionais"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
          </MaterialGrid>
          {hotel?.room_rules?.additional_room?.charge_enabled !== false && (
            <FieldArray
              name={`${field}.room_rules.additional_room.pricing_tiers`}
              component={renderPricingTiers}
            />
          )}

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Inventário</Typography>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Total de quartos deste hotel"
                name={`${field}.room_rules.total_rooms_available`}
                type="number"
                helperText="0 = sem limite"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Máximo de quartos por inscrição"
                name={`${field}.room_rules.max_rooms_per_registration`}
                type="number"
                helperText="0 = sem limite"
              />
            </MaterialGrid>
          </MaterialGrid>

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Limite de acompanhantes não pagantes</Typography>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Máximo de não pagantes no quarto 1"
                name={`${field}.room_rules.first_room.free_companion_limit`}
                type="number"
                validate={[FORM_RULES.required]}
                helperText="0 = sem limite"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={6}>
              <Field
                component={InputLabelOutlined}
                label="Máximo de não pagantes por quarto adicional"
                name={`${field}.room_rules.additional_room.free_companion_limit`}
                type="number"
                validate={[FORM_RULES.required]}
                helperText="0 = sem limite"
              />
            </MaterialGrid>
          </MaterialGrid>

          <Divider style={{ margin: '20px 0' }} />

          <Typography style={sectionTitleStyle}>Regras para menores</Typography>
          <Box
            style={{
              backgroundColor: '#f5f7fa',
              borderRadius: 8,
              padding: 16,
              marginBottom: 16,
            }}>
            <Typography style={{ fontSize: 13, color: '#666', lineHeight: 1.8 }}>
              Use estes controles para definir se menores ocupam vaga de quarto e se entram na
              cobrança.
            </Typography>
          </Box>
          <MaterialGrid container spacing={2}>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.room_rules.minors_free`}
                label="Menores são não pagantes"
                firstState="Sim"
                secondState="Não"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.room_rules.minors_count_towards_room_limit`}
                label="Menores contam no limite do quarto"
                firstState="Contam"
                secondState="Não contam"
              />
            </MaterialGrid>
            <MaterialGrid item xs={12} md={4}>
              <Field
                component={InputSwitchLarge}
                name={`${field}.room_rules.minors_count_towards_payment`}
                label="Menores contam para pagamento"
                firstState="Contam"
                secondState="Não contam"
              />
            </MaterialGrid>
          </MaterialGrid>
        </Box>
      </Collapse>
    </Box>
  );
}

HotelCard.propTypes = {
  field: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  hotel: PropTypes.object,
  onRemove: PropTypes.func.isRequired,
  defaultExpanded: PropTypes.bool,
};

const buildEmptyHotel = (index) => ({
  id: '',
  name: '',
  description: '',
  active: true,
  room_rules: {
    ...DEFAULT_HOTEL_ROOM_RULES,
    first_room: { ...DEFAULT_HOTEL_ROOM_RULES.first_room },
    additional_room: { ...DEFAULT_HOTEL_ROOM_RULES.additional_room },
  },
  __displayIndex: index,
});

function renderHotels({ fields, formHotels = [] }) {
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography style={{ fontSize: 16, fontWeight: '700', color: '#1a3a5c' }}>
            Hotéis disponíveis
          </Typography>
          <Typography style={{ fontSize: 13, color: '#666' }}>
            Cada hotel tem seu próprio inventário e regras de cobrança. O participante poderá
            escolher entre os hotéis disponíveis.
          </Typography>
        </Box>
        <PrimaryButton type="button" onClick={() => fields.push(buildEmptyHotel(fields.length))}>
          Adicionar hotel
        </PrimaryButton>
      </Box>

      {fields.length === 0 ? (
        <Box
          style={{
            backgroundColor: '#f5f7fa',
            borderRadius: 8,
            padding: 16,
            marginBottom: 16,
            textAlign: 'center',
          }}>
          <Typography style={{ fontSize: 13, color: '#666' }}>
            Nenhum hotel configurado. Clique em "Adicionar hotel" para começar.
          </Typography>
        </Box>
      ) : null}

      {fields.map((field, index) => (
        <HotelCard
          key={field}
          field={field}
          index={index}
          hotel={formHotels[index]}
          onRemove={() => fields.remove(index)}
          defaultExpanded={fields.length <= 1 || index === fields.length - 1}
        />
      ))}
    </Box>
  );
}

renderHotels.propTypes = {
  fields: PropTypes.object,
  formHotels: PropTypes.array,
};

class RoomRules extends Component {
  onSubmit = (values) => {
    values.submitFromButton = true;
    this.props.onClickProgress(values);
  };

  render() {
    const formHotels = this.props.formValues?.copa_sarau_config?.hotels || [];

    return (
      <>
        <GenericConfirmationCard
          open={this.props.openConfirmation}
          cancelClose={this.props.handleCancelClose}
          general={this.props.general}
          title={this.props.confirmationTitle}
          text={this.props.confirmationSubtitle}
          confirmButtonColor="primary"
          confirmButtonLabel={this.props.buttonConfirmationLabel}
          onClose={this.props.handleCancelClose}
          onClick={this.props.closeModal}
        />
        <Form role="form" onSubmit={this.props.handleSubmit(this.onSubmit)} noValidate>
          <div
            style={{
              overflowY: 'scroll',
              height: isMobile ? 'calc(100vh - 240px)' : 'calc(100vh - 355px)',
              overflowX: 'hidden',
              padding: '0 10px',
            }}>
            <div style={{ marginTop: isMobile ? 0 : 20 }}>
              <Typography
                style={{ fontSize: 16, fontWeight: '700', marginBottom: 8 }}
                variant="subtitle2">
                Hotéis e regras dos quartos
              </Typography>
              <Typography style={{ fontSize: 13, color: '#666', marginBottom: 20 }}>
                Configure cada hotel com sua capacidade, preços e estoque de quartos. O participante
                verá os hotéis disponíveis e poderá escolher.
              </Typography>

              <FieldArray
                name="copa_sarau_config.hotels"
                component={renderHotels}
                formHotels={formHotels}
              />

              <Divider style={{ margin: '24px 0' }} />

              <Typography style={sectionTitleStyle}>Reservas pendentes (global)</Typography>
              <MaterialGrid container spacing={2}>
                <MaterialGrid item xs={12} md={6}>
                  <Field
                    component={InputLabelOutlined}
                    label="TTL de reserva pendente (horas)"
                    name="copa_sarau_config.pending_reservation_ttl_hours"
                    type="number"
                    validate={[FORM_RULES.required]}
                    helperText="Tempo que uma reserva pendente segura o quarto antes de liberar. Padrão: 24h"
                  />
                </MaterialGrid>
              </MaterialGrid>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '16px 10px',
              borderTop: '1px solid #eee',
            }}>
            <SecondaryButton type="button" onClick={this.props.prevClick}>
              Voltar
            </SecondaryButton>
            <PrimaryButton type="submit">Salvar e continuar</PrimaryButton>
          </div>
        </Form>
      </>
    );
  }
}

RoomRules.propTypes = {
  buttonConfirmationLabel: PropTypes.any,
  closeModal: PropTypes.func,
  confirmationSubtitle: PropTypes.any,
  confirmationTitle: PropTypes.any,
  formValues: PropTypes.any,
  general: PropTypes.any,
  handleCancelClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  onClickProgress: PropTypes.func,
  openConfirmation: PropTypes.any,
  prevClick: PropTypes.func,
};

const mapStateToProps = (state) => {
  return {
    formValues: getFormValues('contactsSurveysForm')(state),
    general: state.general,
  };
};

const mapDispatchToProps = (dispatch) => {
  return bindActionCreators({}, dispatch);
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: 'contactsSurveysForm',
    destroyOnUnmount: false,
    forceUnregisterOnUnmount: true,
  })(RoomRules)
);
